import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, ShieldCheck, Loader2, CheckCircle2, RefreshCw } from 'lucide-react';
import PaymentIframeModal, { PaymentBreakdown } from '../components/PaymentIframeModal';

const API_URL = (import.meta.env.VITE_API_URL || '/api') as string;

interface FormData {
  token: string;
  title: string;
  orgName: string;
  regNumber: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  planName: string;
  setupFeeNet: number;
  recurringNet: number;
  monthlyPriceNet?: number;
  discount?: number;
  discountMonths?: number;
  oneTimePayment?: boolean;
  installments?: number;
  firstChargeNet: number;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'failed' | 'expired';
  paidAt?: string;
}

const vat = (net: number) => Math.round(net * 0.18 * 100) / 100;
const gross = (net: number) => Math.round(net * 1.18 * 100) / 100;
const fmt = (n: number) => `₪${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function PublicPaymentForm() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Editable org details
  const [fields, setFields] = useState({
    orgName: '', regNumber: '', contactName: '', contactEmail: '', contactPhone: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeOpen, setIframeOpen] = useState(false);
  const [iframeDetails, setIframeDetails] = useState<PaymentBreakdown | undefined>(undefined);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/payment-forms/public/${token}`);
        const d = res.data as FormData;
        setData(d);
        setFields({
          orgName: d.orgName || '',
          regNumber: d.regNumber || '',
          contactName: d.contactName || '',
          contactEmail: d.contactEmail || '',
          contactPhone: d.contactPhone || '',
        });
        if (d.status === 'paid') setPaid(true);
        axios.post(`${API_URL}/payment-forms/public/${token}/view`).catch(() => {});
      } catch (e: any) {
        setLoadError(e?.response?.data?.error || 'הטופס לא נמצא או שאינו זמין');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const startPayment = async () => {
    setError('');
    if (!fields.orgName.trim()) { setError('יש להזין שם עמותה'); return; }
    setSubmitting(true);
    try {
      // Persist confirmed/edited details first
      await axios.post(`${API_URL}/payment-forms/public/${token}/submit`, fields);
      const res = await axios.post(`${API_URL}/payment-forms/public/${token}/payment-session`);
      const d = res.data;
      const lines = (d.lines && d.lines.length)
        ? d.lines.map((l: any) => ({ label: l.label, amount: l.amount, note: l.note }))
        : [{ label: data?.planName || 'תשלום', amount: d.net as number }];
      setIframeDetails({
        item: data?.title || 'תשלום',
        lines,
        net: d.net,
        vat: d.vat,
        gross: d.gross,
        subline: (!d.oneTime && d.recurringNet > 0)
          ? `מהחודש הבא: ${fmt(d.recurringGross)}/חודש (חיוב חוזר)`
          : undefined,
      });
      setIframeUrl(d.url);
      setIframeOpen(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה ביצירת התשלום');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow p-8 text-center max-w-sm">
          <div className="text-4xl mb-3">⚠️</div>
          <p className="text-gray-700">{loadError || 'הטופס לא נמצא'}</p>
        </div>
      </div>
    );
  }

  if (paid) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-black text-gray-800">התשלום עבר בהצלחה!</h1>
          <p className="text-gray-500 mt-2">תודה — קיבלנו את התשלום.</p>
          {!data.oneTimePayment && data.recurringNet > 0 && (
            <div className="mt-4 text-sm text-gray-600 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 inline-flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-emerald-600" />
              אמצעי התשלום נשמר לחיוב חודשי של {fmt(gross(data.recurringNet))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-l from-emerald-600 to-green-600 rounded-2xl p-6 text-white text-center shadow-lg mb-5">
          <CreditCard className="w-10 h-10 mx-auto mb-2" />
          <h1 className="text-xl font-black">{data.title || 'טופס תשלום'}</h1>
          <p className="text-emerald-100 text-sm mt-1">{data.orgName}</p>
        </div>

        {/* Org details */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-5">
          <h2 className="font-bold text-gray-800 mb-3">פרטי העמותה</h2>
          <div className="space-y-3">
            <Field label="שם עמותה" value={fields.orgName} onChange={v => setFields(f => ({ ...f, orgName: v }))} />
            <Field label="ח.פ / מס' עמותה" value={fields.regNumber} onChange={v => setFields(f => ({ ...f, regNumber: v }))} />
            <Field label="איש קשר" value={fields.contactName} onChange={v => setFields(f => ({ ...f, contactName: v }))} />
            <Field label="אימייל" value={fields.contactEmail} onChange={v => setFields(f => ({ ...f, contactEmail: v }))} ltr />
            <Field label="טלפון" value={fields.contactPhone} onChange={v => setFields(f => ({ ...f, contactPhone: v }))} ltr />
          </div>
        </div>

        {/* Payment summary */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-5">
          <h2 className="font-bold text-gray-800 mb-3">סעיפי תשלום</h2>
          <div className="space-y-1.5 text-sm">
            {data.setupFeeNet > 0 && (
              <Row label="דמי הקמה (חד פעמי)" value={fmt(data.setupFeeNet)} />
            )}
            {data.recurringNet > 0 && (
              <Row label={`${data.planName || 'תשלום'}${data.oneTimePayment ? '' : ' — חודש ראשון'}`} value={fmt(data.recurringNet)} />
            )}
            {data.setupFeeNet <= 0 && data.recurringNet <= 0 && (
              <Row label={`${data.planName || 'תשלום'} (לפני מע״מ)`} value={fmt(data.firstChargeNet)} />
            )}
            <Row label="מע״מ 18%" value={fmt(vat(data.firstChargeNet))} />
            <div className="border-t my-2" />
            <Row label="סה״כ לתשלום היום" value={fmt(gross(data.firstChargeNet))} bold />
          </div>
          {data.oneTimePayment && Number(data.installments) > 1 && (
            <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-start gap-2">
              <CreditCard className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>ניתן לפצל עד <b>{data.installments} תשלומים</b> בכרטיס — תבחרו את מספר התשלומים בעמוד התשלום.</span>
            </div>
          )}
          {!data.oneTimePayment && data.recurringNet > 0 && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
              <RefreshCw className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                אמצעי התשלום יישמר לחיוב חודשי חוזר של <b>{fmt(gross(data.recurringNet))}</b> החל מהחודש הבא.
                {Number(data.discountMonths) > 0 && Number(data.monthlyPriceNet) > data.recurringNet && (
                  <> לאחר {data.discountMonths} חודשים → <b>{fmt(gross(Number(data.monthlyPriceNet)))}</b>/חודש.</>
                )}
              </span>
            </div>
          )}
        </div>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">{error}</div>}

        <button
          onClick={startPayment}
          disabled={submitting}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
          {submitting ? 'מכין תשלום…' : `לתשלום ${fmt(gross(data.firstChargeNet))}`}
        </button>

        <p className="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> תשלום מאובטח בתקן PCI דרך Tranzila
        </p>
      </div>

      <PaymentIframeModal
        url={iframeUrl}
        open={iframeOpen}
        onClose={() => setIframeOpen(false)}
        title="תשלום מאובטח"
        details={iframeDetails}
        onSuccess={() => { setIframeOpen(false); setPaid(true); }}
        onFail={() => { setIframeOpen(false); setError('התשלום נכשל. נסו שוב או פנו לתמיכה.'); }}
      />
    </div>
  );
}

function Field({ label, value, onChange, ltr }: { label: string; value: string; onChange: (v: string) => void; ltr?: boolean }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        dir={ltr ? 'ltr' : 'rtl'}
        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400 outline-none"
      />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? 'font-bold text-gray-800' : 'text-gray-500'}>{label}</span>
      <span className={bold ? 'font-bold text-gray-800' : 'text-gray-700'}>{value}</span>
    </div>
  );
}
